/**
 * Honeybadger Error Tracking Configuration
 * @memberof @albert-io/notifier
 */

import React from 'react';
import {Honeybadger, HoneybadgerErrorBoundary} from '@honeybadger-io/react';

import {ERROR_TYPES} from 'client/constants';
import ErrorPage from 'client/static/Error/ErrorPage.react';
// The session leaf module, NOT '@albert-io/mandark/authentication' or SessionStore:
// both of those import this module back (authentication -> notifier for decode()
// reporting), which is a module cycle.
import {getSession} from '@albert-io/mandark/authentication/session';
import {getOwnAppVersion} from 'lib/appVersion';
import {shouldReportError, isCurrentUrlMatch, isPageTranslated} from './filters';
import {getHoneybadgerEnvironment, isKnownReportingHost} from './environment';
import {shouldSuppressStaleBundleNotice} from './staleBundle';
import {CONTENT_FILTERS, scrubBreadcrumbOpts, scrubContext} from './scrub';

// configuration

const RELEASE_VERSION = getOwnAppVersion();

// Normalize custom tags to array format as they can be string or array,
function normalizeCustomTags(customTags) {
  if (!customTags) return [];
  if (Array.isArray(customTags)) return customTags.filter(Boolean);
  return [customTags];
}

// Build environment tags as an array for Honeybadger
// Honeybadger now accepts tags as an array instead of comma-separated string

const buildEnvironmentTags = (customTags, userAgent) => {
  const isIpad = /iPad/.test(userAgent);
  const isDennis = isCurrentUrlMatch('/dennis');

  const customTagsArray = normalizeCustomTags(customTags);

  // Build complete tags array
  const tags = [
    RELEASE_VERSION,
    getSession().isValid() ? getSession().getAccountType() : 'unauthenticated',
    ...(isIpad ? ['ipad'] : []),
    ...(isDennis ? ['dennis'] : []),
    ...(isPageTranslated() ? ['translated'] : []),
    ...customTagsArray
  ];

  // Remove duplicates and empty values
  return [...new Set(tags.filter(Boolean))];
};

const buildErrorContext = (existingContext) => ({
  ...existingContext,
  user_id: getSession().isValid() ? getSession().getId() : null,
  hasTranslate: isPageTranslated(),
  route: {pathname: window.location.pathname}
});

const initializeHoneybadger = () => {
  // V8 keeps 10 frames, which React and router internals fill before any app frame.
  Error.stackTraceLimit = 50;

  Honeybadger.configure({
    apiKey: process.env.HONEYBADGER_API_KEY,
    environment: getHoneybadgerEnvironment(),
    filters: CONTENT_FILTERS,
    projectRoot: 'webpack:///./',
    reportData: true,
    revision: RELEASE_VERSION,
    maxErrors: 20,
    breadcrumbsEnabled: {
      dom: true,
      network: true,
      navigation: true,
      console: false
    },
    maxBreadcrumbs: 50
  });

  // Breadcrumbs bypass the SDK's `filters` and attach after beforeNotify runs,
  // so they can only be scrubbed at creation (see ./scrub). Shadowing the
  // instance method covers the SDK's own instrumentation.
  const addBreadcrumb = Honeybadger.addBreadcrumb.bind(Honeybadger);
  Honeybadger.addBreadcrumb = (message, opts) => addBreadcrumb(message, scrubBreadcrumbOpts(opts));

  Honeybadger.beforeNotify((notice) => {
    const userAgent = navigator.userAgent || '';
    notice.tags = buildEnvironmentTags(notice.tags, userAgent);
    // The SDK sends context verbatim — its `filters` cover only url/params/session.
    notice.context = scrubContext(buildErrorContext(notice.context));
    return shouldReportError(notice) && !shouldSuppressStaleBundleNotice(RELEASE_VERSION);
  });
};

// One flag gates BOTH init and notify() so they can't drift. The host gate
// matters because the key is baked into every bundle — without it, localhost
// and rehosted copies (translation proxies, mirrors) would report too.
const isReportingEnabled = Boolean(process.env.HONEYBADGER_API_KEY) && isKnownReportingHost();

if (isReportingEnabled) {
  initializeHoneybadger();
} else if (process.env.HONEYBADGER_API_KEY) {
  // Fails closed — a keyed build on an unlisted host reports NOTHING, so warn
  // loudly to make the missing ENVIRONMENT_RULES entry discoverable.
  console.warn(
    `Honeybadger reporting is disabled for host "${global.window?.location?.hostname}". ` +
      'If this is a legitimate serving host, add it to ENVIRONMENT_RULES in ' +
      '@albert-io/notifier services/honeybadger/environment.js.'
  );
}

/**
 * Report errors to Honeybadger - lightweight wrapper around Honeybadger.notify
 *
 * @example
 * notifier.notify(error, {
 *   message: 'Failed to save',
 *   tags: 'ComponentName',
 *   fingerprint: 'unique-error-id'
 * })
 */
const notifier = {
  notify(error, options) {
    if (!isReportingEnabled) {
      console.warn('Honeybadger reporting is disabled; skipping error report.', error);
      return;
    }

    Honeybadger.notify(error, options);
  }
};

/**
 * onError handler for react-error-boundary boundaries. Such a boundary stops
 * React's error propagation, so the root HoneybadgerErrorBoundary never sees
 * the error — without this callback the crash is swallowed unreported (#19427).
 */
export const notifyBoundaryError = (message, tags) => (error, info) =>
  notifier.notify(error, {message, tags, context: {componentStack: info?.componentStack}});

export const ErrorBoundary = ({children}) => (
  <HoneybadgerErrorBoundary
    honeybadger={Honeybadger}
    ErrorComponent={() => <ErrorPage errorCode={ERROR_TYPES.GENERIC} />}
  >
    {children}
  </HoneybadgerErrorBoundary>
);

export default notifier;
